import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { AlertTitle, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

import PlaybackSidebar from "./components/PlaybackSidebar";
import PlaybackHeader from "./components/PlaybackHeader";
import PlaybackVideoArea from "./components/PlaybackVideoArea";
import PlaybackControlBar from "./components/PlaybackControlBar";
import PlaybackTimeline from "./components/PlaybackTimeline";
import PlaybackPlaceholder from "./components/PlaybackPlaceholder";
import SnapshotModal from "./components/modal/SnapshotModal";

import {
  getTodayStartTimestamp,
  getDayStartTimestamp,
  getInitialPlaybackTime,
  getPlaybackDayRange,
  getPlaybackMonthRange,
  getPlaybackEventDates,
  getPlaybackRecordList,
  findPlayableRecordAtTime,
  findCoveringRecordAtTime,
  partitionSurveillanceDevices,
  isNvrSubDevice,
  toOsdDisplayPtsMs,
  toMediaPtsMs,
  TIMELINE_SCALE_CONFIG,
  SPEED_OPTIONS,
} from "./utils/playbackHelper";
import {
  createDefaultTrimRange,
  isValidTrimRange,
  trimRangeHasRecording,
} from "./utils/trimRange";

import GlobalAlert from "components/Notification";
import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";
import { useAuth } from "contexts/AuthContext";
import { resolveStreamConfig } from "pages/Surveillance/Liveview/lib/streamConfig";
import { useCreatePlaybackStreamCommand } from "./hooks/useCreatePlaybackStreamCommand";
import { usePlaybackStream } from "./hooks/usePlaybackStream";
import { CLIP_DOWNLOAD_CANCELLED, useClipDownload } from "./hooks/useClipDownload";

const SEEK_DEBOUNCE_MS = 400;
const TRIM_TIME_SCALES = new Set(["10M", "1H"]);
const CLIP_STREAM_IDS = { HD: 1, SD: 2 };

const pad2 = (value) => String(value).padStart(2, "0");
const formatFileDate = (date) => `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;
const formatFileTime = (date) => `${pad2(date.getHours())}${pad2(date.getMinutes())}${pad2(date.getSeconds())}`;

const SurveillancePlayback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const [devices, setDevices] = useState([]);
  const [nvrs, setNvrs] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  // Audio starts off: the browser only lets it out after a user gesture, and
  // the AV clock only gates video once there is audio to stay in sync with.
  const [muted, setMuted] = useState(true);
  const [playbackRecordsLoading, setPlaybackRecordsLoading] = useState(false);
  const [playbackRecords, setPlaybackRecords] = useState(null);
  const [playbackRecordsRequestUuid, setPlaybackRecordsRequestUuid] = useState(null);
  const [calendarRecordsRequestUuid, setCalendarRecordsRequestUuid] = useState(null);
  const [calendarEventDates, setCalendarEventDates] = useState([]);
  const [hasActiveStream, setHasActiveStream] = useState(false);
  // Captured frame awaiting confirmation: { blob, url, fileName }.
  const [snapshot, setSnapshot] = useState(null);
  const [digitalZoom, setDigitalZoom] = useState(100);
  // Trim & download: range in unix seconds, at most TRIM_MAX_SECONDS long.
  const [isTrimming, setIsTrimming] = useState(false);
  const [trimRange, setTrimRange] = useState(null);
  const [alertState, setAlertState] = useState({ open: false, severity: "info", message: "" });
  const [playbackTime, setPlaybackTime] = useState(() => getTodayStartTimestamp());
  // OSD-aligned clock for NVR labels (updates on display-second boundaries).
  const [osdDisplayTime, setOsdDisplayTime] = useState(() => getTodayStartTimestamp());
  const [selectedDate, setSelectedDate] = useState(() => getTodayStartTimestamp());
  const [timeScale, setTimeScale] = useState("1H");
  const [playbackSpeed, setPlaybackSpeed] = useState("1/16X");

  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [hoverTimeline, setHoverTimeline] = useState(null);
  const [hoveredRecordKey, setHoveredRecordKey] = useState(null);

  const timelineRef = useRef(null);
  const timelineDragRef = useRef(null);
  const activeDeviceIdRef = useRef("");
  const lastPlaybackQueryRef = useRef("");
  const pendingManualPlaybackTimeRef = useRef(null);
  const videoRef = useRef(null);
  const playbackTimeRef = useRef(null);
  const seekDebounceRef = useRef(null);
  const playbackRecordsRef = useRef(null);
  const clockLatchedRef = useRef(false);
  // Precise media-clock PTS (ms); playbackTime state only updates on second change.
  const playheadPtsRef = useRef(null);

  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { token: authToken } = useAuth();
  const devicesUuid = useMemo(() => uuidv4(), []);

  const selectedDeviceIds = useMemo(
    () =>
      searchParams
        .getAll("deviceId")
        .flatMap((value) => value.split(","))
        .filter(Boolean),
    [searchParams]
  );
  const selectedDeviceId = selectedDeviceIds[0] || "";
  const isMultiChannel = selectedDeviceIds.length > 1;
  const selectedDevice = useMemo(() => {
    return [...devices, ...nvrs.flatMap((nvr) => nvr.cameras || [])].find(
      (device) => String(device.id) === selectedDeviceId
    );
  }, [devices, nvrs, selectedDeviceId]);

  // NVR sub-device: Header PTS leads camera burn-in OSD by a fixed ~1.1s.
  const isNvrChannel = isNvrSubDevice(selectedDevice);

  const resetPlaybackForCamera = useCallback(() => {
    const today = getTodayStartTimestamp();
    setSelectedDate(today);
    setPlaybackTime(today);
    setOsdDisplayTime(today);
    setTimeScale("1H");
    setIsPlaying(false);
    setPlaybackRecords(null);
    setPlaybackRecordsLoading(true);
    setPlaybackRecordsRequestUuid(null);
    setCalendarRecordsRequestUuid(null);
    setCalendarEventDates([]);
    setHasActiveStream(false);
    setDigitalZoom(100);
    pendingManualPlaybackTimeRef.current = null;
    if (seekDebounceRef.current) {
      window.clearTimeout(seekDebounceRef.current);
      seekDebounceRef.current = null;
    }
  }, []);

  // Media-clock driven timeline: only advances when the player presents a frame.
  // No setInterval / Date.now() accumulation — stalls freeze the playhead.
  const onPlayheadPts = useCallback((ptsMs) => {
    if (!Number.isFinite(ptsMs)) return;
    playheadPtsRef.current = ptsMs;
    if (!clockLatchedRef.current) {
      clockLatchedRef.current = true;
    }
    // Needle / seek stay on raw media PTS (second floor of raw).
    setPlaybackTime((prevTime) => (
      Math.floor(ptsMs / 1000) === Math.floor(prevTime / 1000) ? prevTime : ptsMs
    ));
    // Clock labels use OSD-compensated time; floor on *display* seconds so
    // the -1100ms NVR offset cannot leave the label one second behind the picture.
    const displayPts = toOsdDisplayPtsMs(ptsMs, isNvrChannel);
    setOsdDisplayTime((prevDisplay) => (
      Math.floor(displayPts / 1000) === Math.floor(prevDisplay / 1000) ? prevDisplay : displayPts
    ));
  }, [isNvrChannel]);

  const getTimelineUiMs = useCallback(
    () => playheadPtsRef.current ?? playbackTimeRef.current,
    []
  );

  const realmId = auth.realm?.id || "";
  const selectedOrgId = auth.orgs?.id || "";
  const selectedSiteId = auth.sites?.id || "";
  const resolvedOrgId = standalone ? realmId : selectedOrgId;
  const entId = standalone === false ? realmId : null;

  const deviceSiteId = selectedSiteId || selectedDevice?.siteId || "";
  const streamConfig = useMemo(
    () => resolveStreamConfig({ device: selectedDevice, authToken }),
    [selectedDevice, authToken]
  );

  const { requestCreatePlaybackStream, cancelPending } =
    useCreatePlaybackStreamCommand({
      standalone,
      entId,
      orgId: resolvedOrgId,
      siteId: deviceSiteId,
      isAuthReady,
    });

  const canCreateStream = Boolean(
    selectedDevice?.id &&
      !isMultiChannel &&
      streamConfig.accessToken &&
      deviceSiteId &&
      isAuthReady
  );

  const {
    status: streamStatus,
    start: startPlaybackStream,
    pause: pausePlaybackStream,
    resume: resumePlaybackStream,
    stop: stopPlaybackStream,
    awaitingPicture,
  } = usePlaybackStream({
    videoRef,
    deviceId: selectedDevice?.id || "",
    deviceSiteId,
    accessToken: canCreateStream ? streamConfig.accessToken : "",
    streamId: streamConfig.streamId || 1,
    requestCreatePlaybackStream: canCreateStream
      ? requestCreatePlaybackStream
      : undefined,
    enabled: canCreateStream,
    muted,
    onPlayheadPts,
    getTimelineUiMs,
  });

  // Display-only clocks: driven by OSD-second boundaries (see onPlayheadPts).
  // Fallback when not yet latched: derive from raw playbackTime.
  const displayPlaybackTime = Number.isFinite(osdDisplayTime)
    ? osdDisplayTime
    : toOsdDisplayPtsMs(playbackTime, isNvrChannel);

  const handleSnapshot = useCallback(async () => {
    const video = videoRef.current;
    if (
      !video ||
      video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      !video.videoWidth ||
      !video.videoHeight
    ) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch (error) {
      return;
    }

    const frameTime = new Date(displayPlaybackTime);
    const pad = (value) => String(value).padStart(2, "0");
    const stamp = `${frameTime.getFullYear()}${pad(frameTime.getMonth() + 1)}${pad(frameTime.getDate())}-${pad(frameTime.getHours())}${pad(frameTime.getMinutes())}${pad(frameTime.getSeconds())}`;
    const safeName = (selectedDevice?.name || selectedDevice?.id || "camera")
      .toString()
      .replace(/[^a-z0-9_-]+/gi, "-");
    const fileName = `${safeName}-snapshot-${stamp}.jpg`;
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.95);
    });
    if (!blob) return;
    setSnapshot({ blob, url: URL.createObjectURL(blob), fileName });
  }, [displayPlaybackTime, selectedDevice?.id, selectedDevice?.name]);

  const closeSnapshot = useCallback(() => setSnapshot(null), []);

  useEffect(() => () => {
    if (snapshot?.url) URL.revokeObjectURL(snapshot.url);
  }, [snapshot]);

  const handleSaveSnapshot = useCallback(async () => {
    if (!snapshot) return;
    const { blob, fileName } = snapshot;
    try {
      if (window.showSaveFilePicker) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: "JPEG image", accept: { "image/jpeg": [".jpg", ".jpeg"] } }],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        // Revoking synchronously can cancel the download in Firefox/Safari.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      closeSnapshot();
    } catch (error) {
      // Cancelling the system dialog keeps the preview open for another try.
      if (error?.name !== "AbortError") {
        console.error("Failed to save playback snapshot", error);
      }
    }
  }, [snapshot, closeSnapshot]);

  // Separate command instance: seeks call cancelPending() on the player's one.
  const {
    requestCreatePlaybackStream: requestClipStream,
    cancelPending: cancelClipRequests,
  } = useCreatePlaybackStreamCommand({
    standalone,
    entId,
    orgId: resolvedOrgId,
    siteId: deviceSiteId,
    isAuthReady,
  });
  const {
    download: downloadClip,
    cancel: cancelClipDownload,
    exporting: clipExporting,
    progress: clipProgress,
  } = useClipDownload({
    requestCreatePlaybackStream: requestClipStream,
    cancelPending: cancelClipRequests,
  });

  const showAlert = useCallback((severity, message) => {
    setAlertState({ open: true, severity, message });
  }, []);

  const exitTrim = useCallback(() => {
    cancelClipDownload();
    setIsTrimming(false);
    setTrimRange(null);
  }, [cancelClipDownload]);

  useEffect(() => {
    exitTrim();
  }, [selectedDeviceId, exitTrim]);

  const handleToggleTrim = useCallback(() => {
    if (isTrimming) {
      exitTrim();
      return;
    }
    const playheadMs = getTimelineUiMs() ?? playbackTime;
    setTrimRange(createDefaultTrimRange(playheadMs / 1000, Date.now() / 1000));
    setIsTrimming(true);
    if (!TRIM_TIME_SCALES.has(timeScale)) setTimeScale("1H");
    showAlert("info", (
      <>
        <AlertTitle>Reminder</AlertTitle>
        Please select a video clip of up to 5 minutes to trim and download.
      </>
    ));
  }, [exitTrim, getTimelineUiMs, isTrimming, playbackTime, showAlert, timeScale]);

  const handleSelectTrimQuality = useCallback(async (quality) => {
    if (!trimRange || !selectedDevice?.id) return;
    if (!isValidTrimRange(trimRange, Date.now() / 1000)) {
      showAlert("warning", "Please select a video clip of up to 5 minutes to trim and download.");
      return;
    }
    if (!trimRangeHasRecording(trimRange, getPlaybackRecordList(playbackRecords))) {
      showAlert("warning", "There are no recordings in the selected range.");
      return;
    }

    const start = new Date(trimRange.startTime * 1000);
    const end = new Date(trimRange.endTime * 1000);
    const safeName = (selectedDevice.name || selectedDevice.id || "camera")
      .toString()
      .replace(/[^a-z0-9_-]+/gi, "-");
    const fileName = `${safeName}-${formatFileDate(start)}-${formatFileTime(start)}-${formatFileTime(end)}-${quality}.mp4`;

    // The picker needs the click's user activation, so it opens before the download.
    let fileHandle = null;
    if (window.showSaveFilePicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: "MP4 video", accept: { "video/mp4": [".mp4"] } }],
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    try {
      const result = await downloadClip({
        deviceId: selectedDevice.id,
        siteId: deviceSiteId,
        streamId: CLIP_STREAM_IDS[quality],
        startTime: trimRange.startTime,
        endTime: trimRange.endTime,
      });
      if (fileHandle) {
        const writable = await fileHandle.createWritable();
        await writable.write(result.blob);
        await writable.close();
      } else {
        const url = URL.createObjectURL(result.blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      if (result.audioDropped) {
        showAlert("warning", "Clip downloaded without audio: this browser cannot encode the audio track.");
      } else if (!result.hasAudio) {
        showAlert("info", "Clip downloaded. The recording has no audio.");
      } else {
        showAlert("success", "Clip downloaded.");
      }
      setIsTrimming(false);
      setTrimRange(null);
    } catch (error) {
      if (error?.message === CLIP_DOWNLOAD_CANCELLED) {
        showAlert("info", "Download cancelled.");
        return;
      }
      console.error("Failed to download playback clip", error);
      showAlert("error", `Failed to download the clip: ${error?.message || "unknown error"}`);
    }
  }, [deviceSiteId, downloadClip, playbackRecords, selectedDevice?.id, selectedDevice?.name, showAlert, trimRange]);

  useEffect(() => {
    playbackTimeRef.current = playbackTime;
  }, [playbackTime]);

  useEffect(() => {
    if (streamStatus === "playback · ended") setIsPlaying(false);
  }, [streamStatus]);

  // Keep OSD clock in sync when playbackTime jumps from seek/calendar (not RVFC).
  useEffect(() => {
    setOsdDisplayTime(toOsdDisplayPtsMs(playbackTime, isNvrChannel));
  }, [playbackTime, isNvrChannel]);

  // New seek/stream: freeze UI latch until the next presented frame.
  useEffect(() => {
    if (awaitingPicture) {
      clockLatchedRef.current = false;
      playheadPtsRef.current = null;
    }
  }, [awaitingPicture]);

  useEffect(() => {
    playbackRecordsRef.current = playbackRecords;
  }, [playbackRecords]);

  /**
   * Resolve covering record → teardown previous stream → createPlaybackStream.
   * Timeline seek only plays when the cursor sits on a recording bar.
   * Empty gaps: keep playhead where it is, stop stream (no snap to first event).
   * Pass allowSnapToNearby=true for camera/day enter auto-start.
   */
  const playAtTime = useCallback(
    (timeMs, { immediate = false, records: recordsOverride, allowSnapToNearby = false } = {}) => {
      if (seekDebounceRef.current) {
        window.clearTimeout(seekDebounceRef.current);
        seekDebounceRef.current = null;
      }

      const run = async () => {
        if (!canCreateStream) {
          console.warn("[Playback] skip createPlaybackStream: missing scope/token/device");
          stopPlaybackStream();
          setHasActiveStream(false);
          return;
        }

        const records = recordsOverride ?? playbackRecordsRef.current;
        const playable = allowSnapToNearby
          ? findPlayableRecordAtTime(records, timeMs)
          : findCoveringRecordAtTime(records, timeMs);

        if (!playable) {
          // Gap on timeline — stay at seek time, do not jump to another segment
          cancelPending();
          stopPlaybackStream();
          setHasActiveStream(false);
          setIsPlaying(false);
          return;
        }

        cancelPending();
        setHasActiveStream(true);
        const ok = await startPlaybackStream({
          startTime: playable.startTime,
          endTime: playable.endTime,
        });
        if (ok) {
          // Keep playhead at stream start (seek position within the covering segment)
          setPlaybackTime(playable.startTime * 1000);
          setIsPlaying(true);
        } else {
          setIsPlaying(false);
        }
      };

      if (immediate) {
        run();
        return;
      }

      seekDebounceRef.current = window.setTimeout(run, SEEK_DEBOUNCE_MS);
    },
    [
      canCreateStream,
      cancelPending,
      startPlaybackStream,
      stopPlaybackStream,
    ]
  );

  // When token/scope becomes ready after records already loaded, start stream
  useEffect(() => {
    if (!canCreateStream || !playbackRecords || playbackRecordsLoading) return;
    if (isMultiChannel) return;
    const hasRecords = getPlaybackRecordList(playbackRecords).length > 0;
    if (!hasRecords) return;
    playAtTime(playbackTime, {
      immediate: true,
      records: playbackRecords,
      allowSnapToNearby: true,
    });
    // Only re-run when readiness flips on — not on every time tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreateStream]);

  const stopPlaybackStreamRef = useRef(stopPlaybackStream);
  const cancelPendingRef = useRef(cancelPending);
  useEffect(() => {
    stopPlaybackStreamRef.current = stopPlaybackStream;
    cancelPendingRef.current = cancelPending;
  }, [stopPlaybackStream, cancelPending]);

  // Clear debounce + stop stream only when leaving the page
  useEffect(
    () => () => {
      if (seekDebounceRef.current) {
        window.clearTimeout(seekDebounceRef.current);
      }
      cancelPendingRef.current?.();
      stopPlaybackStreamRef.current?.();
    },
    []
  );

  // Stop stream when no camera is selected
  useEffect(() => {
    if (selectedDeviceId) return undefined;
    if (seekDebounceRef.current) {
      window.clearTimeout(seekDebounceRef.current);
      seekDebounceRef.current = null;
    }
    cancelPending();
    stopPlaybackStream();
    setHasActiveStream(false);
    return undefined;
  }, [selectedDeviceId, cancelPending, stopPlaybackStream]);

  // Fetch surveillance device tree list
  useEffect(() => {
    if (!isAuthReady || !resolvedOrgId) return;

    const apiKey = selectedSiteId
      ? standalone
        ? "/orgs/:orgId/sites/:siteId/devices.read"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices.read"
      : standalone
        ? "/orgs/:orgId/devices.read"
        : "/ents/:entId/orgs/:orgId/devices.read";
    const restfulParams = selectedSiteId
      ? standalone
        ? { orgId: resolvedOrgId, siteId: selectedSiteId }
        : { entId, orgId: resolvedOrgId, siteId: selectedSiteId }
      : standalone
        ? { orgId: resolvedOrgId }
        : { entId, orgId: resolvedOrgId };

    sendRequest(apiKey, devicesUuid, restfulParams, {
      type: "all",
      group: "surveillance",
    });
  }, [
    isAuthReady,
    resolvedOrgId,
    selectedSiteId,
    standalone,
    entId,
    sendRequest,
    devicesUuid,
  ]);

  // Populate devices and NVRs from data manager
  useEffect(() => {
    const response = getData(devicesUuid);
    if (!response?.data) return;
    const result = partitionSurveillanceDevices(
      Array.isArray(response.data) ? response.data : []
    );
    setDevices(result.devices);
    setNvrs(result.nvrs);
  }, [dataMap, devicesUuid, getData]);

  // Handle playback records response
  useEffect(() => {
    if (!playbackRecordsRequestUuid || !(playbackRecordsRequestUuid in dataMap)) return;

    const response = getData(playbackRecordsRequestUuid) || { data: { records: [] } };
    const initialPlaybackTime = getInitialPlaybackTime(
      response,
      selectedDate,
      isMultiChannel
    );
    const nextPlaybackTime = pendingManualPlaybackTimeRef.current ?? initialPlaybackTime;
    setPlaybackRecords(response);
    setSelectedDate(getDayStartTimestamp(nextPlaybackTime));
    setPlaybackTime(nextPlaybackTime);
    pendingManualPlaybackTimeRef.current = null;
    setIsPlaying(false);
    setPlaybackRecordsLoading(false);
    setPlaybackRecordsRequestUuid(null);

    const hasRecords = getPlaybackRecordList(response).length > 0;
    if (hasRecords && !isMultiChannel) {
      // Pass response directly — ref sync effect has not run yet in this tick
      playAtTime(nextPlaybackTime, {
        immediate: true,
        records: response,
        allowSnapToNearby: true,
      });
    } else {
      stopPlaybackStream();
      setHasActiveStream(false);
    }
  }, [
    dataMap,
    getData,
    isMultiChannel,
    playbackRecordsRequestUuid,
    playAtTime,
    selectedDate,
    stopPlaybackStream,
  ]);

  useEffect(() => {
    if (!calendarRecordsRequestUuid || !(calendarRecordsRequestUuid in dataMap)) return;

    const response = getData(calendarRecordsRequestUuid);
    setCalendarEventDates(getPlaybackEventDates(response));
    setCalendarRecordsRequestUuid(null);
  }, [calendarRecordsRequestUuid, dataMap, getData]);

  // Fetch playback records when camera is selected
  useEffect(() => {
    const isDeviceChange = activeDeviceIdRef.current !== selectedDeviceId;
    const requestDate = isDeviceChange ? getTodayStartTimestamp() : selectedDate;

    if (isDeviceChange) {
      activeDeviceIdRef.current = selectedDeviceId;
      lastPlaybackQueryRef.current = "";
      if (selectedDeviceId) resetPlaybackForCamera();
    }
    if (!isAuthReady || !resolvedOrgId || !selectedDevice) return;

    const deviceSiteId = selectedSiteId || selectedDevice.siteId;
    if (!deviceSiteId) {
      console.error("Failed to fetch playback records: device site ID is missing.");
      setPlaybackRecords({ data: { records: [] } });
      setPlaybackRecordsLoading(false);
      return;
    }

    const queryKey = `${selectedDeviceId}-${getDayStartTimestamp(requestDate)}`;
    if (lastPlaybackQueryRef.current === queryKey) return;
    lastPlaybackQueryRef.current = queryKey;

    const requestUuid = uuidv4();
    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update";
    const restfulParams = standalone
      ? { orgId: resolvedOrgId, siteId: deviceSiteId, deviceId: selectedDevice.id }
      : { entId, orgId: resolvedOrgId, siteId: deviceSiteId, deviceId: selectedDevice.id };

    let requestSent = false;
    const fetchPlaybackRecords = async () => {
      try {
        setPlaybackRecordsRequestUuid(requestUuid);
        await sendRequest(apiKey, requestUuid, restfulParams, {
          data: {
            command: "getPlaybackRecords",
            params: getPlaybackDayRange(requestDate),
          },
        });
        requestSent = true;
      } catch (error) {
        console.error("Failed to fetch playback records:", error);
        setPlaybackRecords({ data: { records: [] } });
        setPlaybackRecordsRequestUuid(null);
        lastPlaybackQueryRef.current = "";
      } finally {
        if (!requestSent) setPlaybackRecordsLoading(false);
      }
    };

    fetchPlaybackRecords();
  }, [
    entId,
    isAuthReady,
    resolvedOrgId,
    resetPlaybackForCamera,
    selectedDevice,
    selectedDeviceId,
    selectedDate,
    selectedSiteId,
    sendRequest,
    standalone,
  ]);

  const loadCalendarMonth = useCallback(
    (timestamp) => {
      if (!isAuthReady || !resolvedOrgId || !selectedDevice) return;

      const deviceSiteId = selectedSiteId || selectedDevice.siteId;
      if (!deviceSiteId) return;

      const requestUuid = uuidv4();
      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId: deviceSiteId, deviceId: selectedDevice.id }
        : { entId, orgId: resolvedOrgId, siteId: deviceSiteId, deviceId: selectedDevice.id };

      let requestSent = false;
      const fetchCalendarRecords = async () => {
        try {
          setCalendarEventDates([]);
          setCalendarRecordsRequestUuid(requestUuid);
          await sendRequest(apiKey, requestUuid, restfulParams, {
            data: {
              command: "getPlaybackRecords",
              params: getPlaybackMonthRange(timestamp),
            },
          });
          requestSent = true;
        } catch (error) {
          console.error("Failed to fetch calendar playback records:", error);
          setCalendarEventDates([]);
        } finally {
          if (!requestSent) {
            setCalendarRecordsRequestUuid((currentUuid) =>
              currentUuid === requestUuid ? null : currentUuid
            );
          }
        }
      };

      fetchCalendarRecords();
    },
    [
      entId,
      isAuthReady,
      resolvedOrgId,
      selectedDevice,
      selectedSiteId,
      sendRequest,
      standalone,
    ]
  );

  const selectDevice = useCallback(
    (device) => {
      try {
        if (!device?.id) return;
        navigate(`/surveillance/playback?deviceId=${encodeURIComponent(device.id)}`);
      } catch (error) {
        console.error("Failed to select playback camera:", error);
        setPlaybackRecords({ data: { records: [] } });
      } finally {
        setIsPlaying(false);
      }
    },
    [navigate]
  );

  const selectDevices = useCallback(
    (items) => {
      if (items?.[0]) selectDevice(items[0]);
    },
    [selectDevice]
  );

  const shiftPlayback = useCallback((seconds) => {
    setPlaybackTime((time) => {
      const next = time + seconds * 1000;
      playAtTime(next);
      return next;
    });
    setIsPlaying(false);
  }, [playAtTime]);

  const toggleFullscreen = useCallback((event) => {
    const root = event.currentTarget.closest(".playback-player");
    if (root?.requestFullscreen) root.requestFullscreen();
  }, []);

  const timeWindow = useMemo(() => {
    const config = TIMELINE_SCALE_CONFIG[timeScale] || TIMELINE_SCALE_CONFIG["1H"];
    return config.totalMinutes * 60 * 1000;
  }, [timeScale]);

  const updateTimelineFromClientX = useCallback(
    (clientX) => {
      const bounds = timelineRef.current?.getBoundingClientRect();
      const dragStart = timelineDragRef.current;
      if (!bounds || bounds.width <= 0 || !dragStart) return;

      const deltaX = clientX - dragStart.clientX;
      const deltaSeconds = (deltaX / bounds.width) * (timeWindow / 1000);
      const currentCenterTime = dragStart.centerTime - deltaSeconds * 1000;
      dragStart.currentCenterTime = currentCenterTime;
      setPlaybackTime(currentCenterTime);
      setIsPlaying(false);
    },
    [timeWindow]
  );

  const updateHoverTimeline = useCallback(
    (clientX) => {
      const bounds = timelineRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width <= 0) return;

      const offsetX = Math.max(0, Math.min(bounds.width, clientX - bounds.left));
      const position = (offsetX / bounds.width) * 100;
      const timelineStart = playbackTime - timeWindow / 2;
      const time = timelineStart + (position / 100) * timeWindow;
      setHoverTimeline({ offsetX, position, time });
    },
    [playbackTime, timeWindow]
  );

  const handleTimelinePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      updateHoverTimeline(event.clientX);
      timelineDragRef.current = {
        clientX: event.clientX,
        centerTime: playbackTime,
        currentCenterTime: playbackTime,
      };
      setIsDraggingTimeline(true);
    },
    [playbackTime, updateHoverTimeline]
  );

  const seekPlayback = useCallback((currentCenterTime) => {
    setPlaybackTime(currentCenterTime);
    setIsPlaying(false);
    playAtTime(currentCenterTime);
  }, [playAtTime]);

  // Window pointer drag event listeners for smooth scrubbing
  useEffect(() => {
    if (!isDraggingTimeline) return undefined;

    const handlePointerMove = (event) => {
      updateTimelineFromClientX(event.clientX);
    };
    const handlePointerUp = () => {
      const finalCenterTime = timelineDragRef.current?.currentCenterTime;
      if (Number.isFinite(finalCenterTime)) seekPlayback(finalCenterTime);
      timelineDragRef.current = null;
      setIsDraggingTimeline(false);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDraggingTimeline, seekPlayback, updateTimelineFromClientX]);

  const cyclePlaybackSpeed = useCallback((direction) => {
    setPlaybackSpeed((prevSpeed) => {
      const index = SPEED_OPTIONS.indexOf(prevSpeed);
      const nextIndex = Math.max(
        0,
        Math.min(SPEED_OPTIONS.length - 1, (index === -1 ? 0 : index) + direction)
      );
      return SPEED_OPTIONS[nextIndex];
    });
  }, []);

  const handleApplyCalendarTime = useCallback(
    (newTimestamp) => {
      // Calendar shows OSD-aligned time for NVR; convert back to media PTS for Seek.
      const mediaTimestamp = toMediaPtsMs(newTimestamp, isNvrChannel);
      const nextDate = getDayStartTimestamp(mediaTimestamp);
      pendingManualPlaybackTimeRef.current = mediaTimestamp;
      setPlaybackTime(mediaTimestamp);
      if (nextDate !== selectedDate) {
        setSelectedDate(nextDate);
        // Records refetch will call playAtTime when response arrives
      } else {
        pendingManualPlaybackTimeRef.current = null;
        playAtTime(mediaTimestamp, { immediate: true });
      }
      setIsPlaying(false);
    },
    [playAtTime, selectedDate, isNvrChannel]
  );

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      pausePlaybackStream();
      setIsPlaying(false);
      return;
    }
    if (resumePlaybackStream()) {
      setIsPlaying(true);
      return;
    }
    // Stream ended or was never started: restart from the playhead.
    const resumeAt = getTimelineUiMs();
    if (Number.isFinite(resumeAt)) playAtTime(resumeAt, { immediate: true });
  }, [isPlaying, pausePlaybackStream, resumePlaybackStream, getTimelineUiMs, playAtTime]);

  const toggleFavorite = useCallback(
    (deviceId) => {
      if (!deviceId || !isAuthReady || !resolvedOrgId) return;

      const current = [
        ...devices,
        ...nvrs.flatMap((nvr) => nvr.cameras || []),
      ].find((device) => device.id === deviceId)?.favorited === true;
      const nextFavorite = !current;
      const updateDevice = (device) =>
        device.id === deviceId ? { ...device, favorited: nextFavorite } : device;

      setDevices((items) => items.map(updateDevice));
      setNvrs((items) =>
        items.map((nvr) => ({
          ...nvr,
          cameras: (nvr.cameras || []).map(updateDevice),
        }))
      );

      const device = [
        ...devices,
        ...nvrs.flatMap((nvr) => nvr.cameras || []),
      ].find((item) => item.id === deviceId);
      const deviceSiteId = selectedSiteId || device?.siteId;
      if (!deviceSiteId) return;

      const apiKey = standalone
        ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/settings.update"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/settings.update";
      const restfulParams = standalone
        ? { orgId: resolvedOrgId, siteId: deviceSiteId, deviceId }
        : { entId, orgId: resolvedOrgId, siteId: deviceSiteId, deviceId };

      sendRequest(apiKey, uuidv4(), restfulParams, {
        data: { info: { favorite: nextFavorite } },
      });
    },
    [
      devices,
      entId,
      isAuthReady,
      nvrs,
      resolvedOrgId,
      selectedSiteId,
      sendRequest,
      standalone,
    ]
  );

  const hasPlaybackRecords = getPlaybackRecordList(playbackRecords).length > 0;

  return (
    <Box sx={{ flexGrow: 1, display: "flex", height: "100%" }}>
      {/* Left Resource Sidebar */}
      <PlaybackSidebar
        devices={devices}
        nvrs={nvrs}
        onPlayCamera={selectDevice}
        onPlayCameras={selectDevices}
        onToggleFavorite={toggleFavorite}
      />

      {/* Main Player Area */}
      <Box
        className="playback-player"
        sx={{
          backgroundColor: theme.palette.primary.white,
          borderRadius: `0 ${theme.shape.customBorderRadius[4]} ${theme.shape.customBorderRadius[4]} 0`,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          boxShadow: "1px 1px 4px 0 rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
      >
        {selectedDevice && !playbackRecordsLoading && playbackRecords ? (
          <>
            <PlaybackHeader
              deviceName={selectedDevice.name}
              onToggleFullscreen={toggleFullscreen}
            />

              <PlaybackVideoArea
                videoRef={videoRef}
                hasStream={hasActiveStream}
                status={streamStatus}
                zoom={digitalZoom}
              />

            {!hasPlaybackRecords && (
              <Box
                role="status"
                sx={{
                  px: 2,
                  py: 1,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.grey[100],
                  borderTop: "1px solid #d4d4d4",
                  textAlign: "center",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <FormattedMessage
                  id="playback.no_recordings_for_day"
                  defaultMessage="No recordings for this day."
                />
              </Box>
            )}

            <Box
              sx={{
                px: 1.5,
                pt: 0.75,
                pb: 1,
                borderTop: "1px solid #d4d4d4",
                backgroundColor: "#fff",
              }}
            >
              <PlaybackControlBar
                isPlaying={isPlaying}
                onTogglePlay={handleTogglePlay}
                muted={muted}
                onToggleMuted={() => setMuted((prev) => !prev)}
                onSnapshot={handleSnapshot}
                snapshotDisabled={!hasActiveStream}
                zoom={digitalZoom}
                onZoomChange={setDigitalZoom}
                zoomDisabled={!hasActiveStream}
                isTrimming={isTrimming}
                onToggleTrim={handleToggleTrim}
                trimDisabled={!canCreateStream || !hasPlaybackRecords}
                timeScale={timeScale}
                onSelectTimeScale={(scale) => setTimeScale(scale)}
                playbackTime={displayPlaybackTime}
                eventDates={calendarEventDates}
                onApplyTime={handleApplyCalendarTime}
                onCalendarMonthChange={loadCalendarMonth}
                onShiftPlayback={shiftPlayback}
                playbackSpeed={playbackSpeed}
                onCycleSpeed={cyclePlaybackSpeed}
              />

              <PlaybackTimeline
                playbackTime={playbackTime}
                displayPlaybackTime={displayPlaybackTime}
                timeScale={timeScale}
                playbackRecords={playbackRecords}
                timelineRef={timelineRef}
                hoverTimeline={hoverTimeline}
                hoveredRecordKey={hoveredRecordKey}
                onPointerDown={handleTimelinePointerDown}
                onPointerMove={(event) => updateHoverTimeline(event.clientX)}
                onPointerLeave={() => setHoverTimeline(null)}
                onRecordHover={(key) => setHoveredRecordKey(key)}
                onRecordLeave={() => setHoveredRecordKey(null)}
                trim={isTrimming && trimRange ? {
                  range: trimRange,
                  maxTime: Math.floor(Date.now() / 1000),
                  exporting: clipExporting,
                  progress: clipProgress,
                  onRangeChange: setTrimRange,
                  onSelectQuality: handleSelectTrimQuality,
                  onCancelExport: cancelClipDownload,
                } : null}
              />
            </Box>
          </>
        ) : (
          <PlaybackPlaceholder
            isLoading={Boolean(selectedDevice && playbackRecordsLoading)}
          />
        )}
      </Box>
      <SnapshotModal
        open={Boolean(snapshot)}
        snapshot={snapshot?.url}
        onClose={closeSnapshot}
        onSave={handleSaveSnapshot}
      />
      <GlobalAlert
        open={alertState.open}
        severity={alertState.severity}
        message={alertState.message}
        onClose={(_event, reason) => {
          if (reason === "clickaway") return;
          setAlertState((prev) => ({ ...prev, open: false }));
        }}
      />
    </Box>
  );
};

export default SurveillancePlayback;
